
import * as React from 'react';
import { IRenderProps, renderChildrenWithProps } from './RenderProps';
import { graft, Delete, IPatch, MirrorPatch } from '../Graft';
import { mapObject, pickObject } from '../objects';
import { createMemoizer } from '../Memoizer';
import { useShallowMemo } from '../../hooks/useShallowMemo';

/**
 * Props for a component which send a value to a `Receiver` via a relay.
 *
 * @internal
 */
export type IRelaySenderProps<TValue> = TValue;

/**
 * Props passed to the render callback from a `Receiver`.
 *
 * @internal
 */
export interface IRelayReceiverStateProps<TValue> {
  values: {
    [key: string]: TValue | undefined;
  };
}

/**
 * @internal
 */
export interface IRelayReceiverProps<TValue> extends IRenderProps<IRelayReceiverStateProps<TValue>> {
  values: {
    [key: string]: JSX.Element | null
  };
}

/**
 * Represents a pair of component types which form a 'relay'.
 *
 * @internal
 */
export interface IRelay<TValue> {
  /**
   * Component type which sould be rendered to 'send' a value to a `Receiver`.
   */
  Sender: React.ComponentType<IRelaySenderProps<TValue>>;
  /**
   * Component type which should be rendered with one or more `senders` and a render-prop callback to process
   * the values.
   */
  Receiver: React.ComponentType<IRelayReceiverProps<TValue>>;
  /**
   * Hook which can be used within a functional component to send state back to the receiver
   * for this relay.
   */
  useSender: (props: IRelaySenderProps<TValue>) => void;
}

/**
 * Available options when defining a Relay.
 * @internal
 */
export interface IRelayOptions<TValue extends object> {
  /**
   * An override for the behavior to merge an updated value from a `Sender` into an existing value.
   * If the value was not previously set, the patch will be applied to the `defaultValue`.
   */
  merge?: ((current: TValue) => IPatch<{ value: TValue; }>['value']);
  /**
   * An override for the behavior to remove an existing value.
   */
  remove?: ((value: TValue) => IPatch<{ value: TValue; }>['value']);
  /**
   * Whether or not to allow a Sender to be used outside a Receiver.
   * By default, using a Sender outside a Receiver will throw an error.
   */
  allowSenderOutsideReceiver?: boolean;
  /**
   * A filter to limit which props passed to the sender are used for state tracking.
   * Can help when `Sender` instances are routinely wrapped with decorators that
   * wind up passing extra props, such as `children`.
   */
  propsFilter?: Record<keyof TValue, boolean>;
}

/**
 * Creates a pair of component types which work in conjunction to 'relay'
 * a value from 'senders' rendered within child components passed to a 'receiver',
 * which then renders content based on the relayed values.
 *
 * @internal
 */
export function createRelay<TValue extends object>(
  defaultValue: TValue,
  options: IRelayOptions<TValue> = {}
): IRelay<TValue> {
  const {
    merge = (value: TValue) => new MirrorPatch(value),
    remove = () => new Delete<TValue>(),
    allowSenderOutsideReceiver = false,
    propsFilter
  } = options;

  interface IReceiverState {
    values: {
      [key: string]: TValue;
    };
  }

  interface IReceiver {
    receiverState: IReceiverState;
    /**
     * The last state rendered by the Content component.
     * Used to determine if the Content will need to re-render in response to a Sender update.
     */
    lastRenderedState: IReceiverState | undefined;
    /**
     * A callback registered by the Content component that can be used to force-update its state.
     */
    contentSetState: ((state: IReceiverState) => void) | undefined;
    /**
     * Memoizer for mapping the relay state to the callback props,
     * based on the current sender set.
     * This helps ensure that identity equality is not lost in the processing
     * of the final state, to avoid update churn.
     */
    mapStateToProps: (state: IReceiverState) => IRelayReceiverStateProps<TValue>;
    /**
     * Whether or not the receiver is disposed and should no longer be updated.
     */
    isDisposed: boolean | undefined;
  }

  const receiverContext = React.createContext<IReceiver | undefined>(undefined);
  const keyContext = React.createContext<string>('');

  type IRelayReceiverContentProps = Pick<IRelayReceiverProps<TValue>, 'children'> & {
    receiver: IReceiver;
  };

  /**
   * Sub-component of receiver which manages re-rendering of the content.
   * This must *not* be a `PureComponent` because it must always re-render in response to any
   * re-render of its parent or any `setState` call.
   */
  class ReceiverContent extends React.Component<IRelayReceiverContentProps, IReceiverState> {
    constructor(props: IRelayReceiverContentProps) {
      super(props);

      const {
        receiver
      } = this.props;

      this.state = receiver.receiverState;

      receiver.contentSetState = (state: IReceiverState) => this.setState(state);
    }

    public render(): JSX.Element | null {
      const {
        receiver
      } = this.props;

      // During render, record which state was used for rendering, so that 'out-of-band' updates don't actually trigger
      // a cacasding update if the `Receiver` itself already needed to render.
      const state = receiver.lastRenderedState = receiver.receiverState;

      return renderChildrenWithProps(
        this.props.children,
        receiver.mapStateToProps(state));
    }
  }

  /**
   * Implementation of the `Receiver` component.
   */
  class Receiver extends React.PureComponent<IRelayReceiverProps<TValue>> {
    /**
     * State and callbacks from this receiver to be provided to all senders.
     */
    private _receiver: IReceiver;

    constructor(props: IRelayReceiverProps<TValue>) {
      super(props);

      this._receiver = {
        receiverState: {
          values: {}
        },
        lastRenderedState: undefined,
        contentSetState: undefined,
        isDisposed: undefined,
        mapStateToProps: createMemoizer((state: IReceiverState): IRelayReceiverStateProps<TValue> => {
          return {
            // Technically, since the input state derives from this.props, it is safe
            // to capture it as a dependency in this closure.
            values: mapObject(this.props.values, (value: JSX.Element | null, key: string | number): TValue => {
              return key in state.values ? state.values[key] : defaultValue;
            })
          };
        })
      };
    }

    public render(): JSX.Element | null {
      const {
        values
      } = this.props;

      const senderElements = Object.keys(values).map((key: string) => {
        const sender = values[key];

        return (
          <keyContext.Provider key={ key } value={ key }>
            { sender }
          </keyContext.Provider>
        );
      });

      const receiver = this._receiver;

      return (
        <>
          {
            senderElements.length ? (
              <receiverContext.Provider key='context' value={ receiver }>
                { senderElements }
              </receiverContext.Provider>
            ) : null
          }
          {
            // Wrap the render of the children into an element,
            // so that React queues it and invokes it once all senders have rendered.
          }
          <ReceiverContent key='content' children={ this.props.children } receiver={ receiver } />
        </>
      );
    }

    public componentWillUnmount(): void {
      this._receiver.isDisposed = true;
    }
  }

  interface IReceiverSenderProps {
    senderKey: string;
    receiver: IReceiver;
    value: TValue;
  }

  function useSender(props: IRelaySenderProps<TValue>): void {
    const senderKey = React.useContext(keyContext);
    const receiver = React.useContext(receiverContext);

    if (!receiver) {
      if (!allowSenderOutsideReceiver) {
        throw new Error('Cannot render a Sender outside of a available Receiver.');
      }
    }

    const value = useShallowMemo(() => propsFilter ? pickObject(props, propsFilter) : props, props);

    React.useMemo(() => {
      if (receiver) {
        preview({ value, senderKey, receiver });
      }
    }, [value, receiver]);

    React.useLayoutEffect(() => {
      if (receiver) {
        update({ value, senderKey, receiver });
      }
    }, [value, receiver]);

    React.useLayoutEffect(() => {
      return () => {
        if (receiver) {
          unmount({ value, senderKey, receiver });
        }
      };
    }, [receiver]);
  }

  /**
   * A 'proxy' implementation of the `Sender` contract which consumes a 'real' `Sender` implementation
   * via context (assuming it is rendered within a `Receiver` in the hierarchy) and forwards the `value`
   * and assigned `senderKey` to the 'real' `Sender`.
   */
  function Sender(props: IRelaySenderProps<TValue>): JSX.Element | null {
    useSender(props);

    return null;
  }

  /**
   * When the component is constructed or about to be passed new props,
   * writes back to the host `Receiver` component with the new state for this `Sender`.
   * This in-band, and must not invoke `setState` on the host `Receiver`.
   */
  function preview(senderProps: IReceiverSenderProps): void {
    const {
      senderKey,
      receiver
    } = senderProps;

    receiver.receiverState = graft(receiver.receiverState, {
      values: {
        [senderKey]: merge(senderProps.value)
      }
    });
  }

  /**
   * When the component is mounted or updated, writes back to the host `Receiver`
   * component with the new state for this `Sender`.
   * Since this likely occurs out-of-band, it should invoke `setState` to trigger a re-render
   * of the `Receiver` and its callbacks if the final state differs from what was rendered.
   */
  function update(senderProps: IReceiverSenderProps): void {
    const {
      senderKey,
      receiver
    } = senderProps;

    receiver.receiverState = graft(receiver.receiverState, {
      values: {
        [senderKey]: merge(senderProps.value)
      }
    });

    if (receiver.receiverState !== receiver.lastRenderedState) {
      if (receiver.contentSetState) {
        receiver.contentSetState(receiver.receiverState);
      }
    }
  }

  /**
   * When the component is about to unmount, removes the state from
   * this `Sender` from the host `Receiver` component.
   */
  function unmount(senderProps: IReceiverSenderProps): void {
    const {
      senderKey,
      receiver
    } = senderProps;

    receiver.receiverState = graft(receiver.receiverState, {
      values: {
        [senderKey]: remove(senderProps.value)
      }
    });

    if (!receiver.isDisposed && receiver.receiverState !== receiver.lastRenderedState) {
      if (receiver.contentSetState) {
        receiver.contentSetState(receiver.receiverState);
      }
    }
  }

  return {
    Receiver,
    Sender,
    useSender
  };
}
